import { Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { DashboardType } from '../types'
import type { DashboardSubType } from '../types'

/**
 * Badge colours per dashboard type, matching the Figma. Deliberately literal rather than pulled
 * from the theme palette: the theme's primary colour is org-configurable, and these three need to
 * stay distinguishable from each other whatever an org has picked.
 */
const TYPE_COLORS: Record<DashboardType, string> = {
  [DashboardType.HOMEPAGE]: '#2C7BE5',
  [DashboardType.SUMMARY]: '#D6219B',
  [DashboardType.INSIGHTS]: '#0E9BA8',
}

interface TypeBadgeProps {
  type: DashboardType
  /** INSIGHTS only. Appended to the label as "Insights · Report" / "Insights · Dashboard". */
  subType?: DashboardSubType | null
}

/**
 * Coloured badge for a dashboard's type. An INSIGHTS dashboard also shows its sub type, since the
 * two render very differently and the distinction matters when picking one to edit.
 *
 * Note the sub type labels read inverted against the enum names — REPORT displays as "Dashboard"
 * and PAGINATED as "Report". That is not a mistake: it carries over the legacy insight_type labels
 * (`translation.json` insightTypeReport / insightTypePaginated), which the admin screens have
 * always shown that way.
 */
export function TypeBadge({ type, subType }: TypeBadgeProps) {
  const { t } = useTranslation('admin-console')
  const base = t(`dashboards.table.type.${type}`)
  const label =
    type === DashboardType.INSIGHTS && subType
      ? `${base} · ${t(`dashboards.table.subType.${subType}`)}`
      : base

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: TYPE_COLORS[type],
        color: 'common.white',
        fontWeight: 600,
        height: 20,
      }}
    />
  )
}
